import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import Pagination from "@/components/Pagination";
import {
  Scales, ShieldWarning, MagnifyingGlass, CheckCircle, XCircle,
  ArrowSquareOut, ArrowRight, WarningCircle, TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);

const REASON_LABEL = {
  price_hike: "Price Hike",
  extra_scope: "Extra Scope",
  design_change: "Design Change",
  market_variation: "Market Variation",
  other: "Other",
};

const STATUS_META = {
  pending: { label: "Pending Owner Review", cls: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  approved: { label: "Approved", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  rejected: { label: "Rejected", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

export default function BudgetRevisions() {
  const { user } = useAuth();
  const { activeProjectId, activeProject } = useProject();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending_count: 0, pending_delta_sum: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(null); // {rev, action}
  const [notes, setNotes] = useState("");
  const [alsoRejectPO, setAlsoRejectPO] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (activeProjectId) params.project_id = activeProjectId;
      if (status !== "all") params.status = status;
      if (search) params.search = search;
      const { data } = await api.get("/budget-revisions", { params });
      setItems(data.items || []);
      setStats(data.stats || { pending_count: 0, pending_delta_sum: 0 });
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status, activeProjectId]);
  useEffect(() => { const t = setTimeout(() => load(1), 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openReview = (rev, action) => {
    setReviewDialog({ rev, action });
    setNotes("");
    setAlsoRejectPO(false);
  };
  const submitReview = async () => {
    if (!reviewDialog) return;
    const { rev, action } = reviewDialog;
    if (action === "reject" && !notes.trim()) {
      toast.error("Notes are required to reject a revision");
      return;
    }
    setSubmitting(true);
    try {
      const body = { notes };
      if (action === "reject") body.also_reject_po = alsoRejectPO;
      await api.post(`/budget-revisions/${rev.id}/${action}`, body);
      toast.success(action === "approve" ? "Revision approved — BOQ updated" : "Revision rejected");
      setReviewDialog(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (user && !isAdmin) {
    return (
      <div className="p-10 text-center">
        <ShieldWarning size={48} className="mx-auto text-neutral-400 mb-3" />
        <h2 className="font-display text-2xl font-bold mb-2">Owner Only</h2>
        <p className="text-sm text-neutral-500">Budget revisions can only be reviewed by the project owner.</p>
      </div>
    );
  }

  return (
    <div className="p-8 sm:p-10 max-w-[1500px] mx-auto" data-testid="budget-revisions-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Owner Review</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <Scales size={32} className="text-primary" weight="fill" /> Budget Revisions
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            {stats.pending_count} pending request{stats.pending_count === 1 ? "" : "s"} · impact <span className="tabular-nums font-medium text-neutral-800">{fmt(stats.pending_delta_sum)} qty</span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Category, remark, requester…" className="rounded-sm pl-9 h-10" data-testid="revisions-search" />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-10" data-testid="revisions-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Current Limit</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Requested</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Δ</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Reason</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Remark</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">PO</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Requester</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-16 text-neutral-400">No revisions found.</td></tr>
              ) : items.map((r, idx) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                return (
                  <tr key={r.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`revision-row-${r.id}`}>
                    <td className="px-6 py-3 text-neutral-900 font-medium">{r.category}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-600">{fmt(r.current_limit)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-900">{fmt(r.requested_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-[#C5221F]">+{fmt(r.delta)}</td>
                    <td className="px-4 py-3 text-neutral-700 text-xs">{REASON_LABEL[r.reason_category] || r.reason_category}</td>
                    <td className="px-4 py-3 text-neutral-500 text-xs max-w-[220px] truncate" title={r.remark}>{r.remark}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.po_id ? (
                        <Link to={`/pos/${r.po_id}`} className="text-primary hover:underline inline-flex items-center gap-1 font-mono-num" data-testid={`revision-po-link-${r.id}`}>
                          {r.po_number || r.po_id.slice(-6)} <ArrowSquareOut size={10} />
                        </Link>
                      ) : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 text-xs">{r.requested_by_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === "pending" && (
                        <>
                          <Button size="sm" variant="ghost" className="rounded-sm h-8 gap-1 text-[#C5221F] hover:text-[#C5221F] hover:bg-[#FCE8E6]" onClick={() => openReview(r, "reject")} data-testid={`revision-reject-${r.id}`}>
                            <XCircle size={13} weight="bold" />Reject
                          </Button>
                          <Button size="sm" className="rounded-sm h-8 ml-1 gap-1 bg-[#137333] hover:bg-[#0E5C29] text-white" onClick={() => openReview(r, "approve")} data-testid={`revision-approve-${r.id}`}>
                            <CheckCircle size={13} weight="bold" />Approve
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} totalPages={totalPages} onPageChange={(n) => load(n)} testIdPrefix="revisions" />
      </div>

      {/* Review dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={(o) => !o && !submitting && setReviewDialog(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="revision-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {reviewDialog?.action === "approve" ? <><CheckCircle size={20} className="text-[#137333]" /> Approve Revision</> : <><XCircle size={20} className="text-[#C5221F]" /> Reject Revision</>}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog?.action === "approve"
                ? "The BOQ limit will be increased and any linked PO will be moved back to open."
                : "Add notes explaining why the revision is rejected. The PO will remain flagged."}
            </DialogDescription>
          </DialogHeader>
          {reviewDialog && (
            <div className="space-y-3">
              <div className="rounded-sm border border-neutral-200 bg-neutral-50 p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-neutral-500">Category</span><span className="font-medium">{reviewDialog.rev.category}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Current Limit</span><span className="tabular-nums">{fmt(reviewDialog.rev.current_limit)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">New Requested Total</span><span className="tabular-nums font-medium">{fmt(reviewDialog.rev.requested_total)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Delta</span><span className="tabular-nums text-[#C5221F] font-medium">+{fmt(reviewDialog.rev.delta)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Reason</span><span>{REASON_LABEL[reviewDialog.rev.reason_category]}</span></div>
                <div className="pt-1 text-neutral-700">"{reviewDialog.rev.remark}"</div>
              </div>
              <Textarea placeholder={reviewDialog.action === "reject" ? "Notes (required)…" : "Notes (optional)…"} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-sm" data-testid="revision-notes" />
              {reviewDialog.action === "reject" && reviewDialog.rev.po_id && (
                <label className="flex items-center gap-2 text-xs text-neutral-600">
                  <input type="checkbox" checked={alsoRejectPO} onChange={(e) => setAlsoRejectPO(e.target.checked)} data-testid="revision-also-reject-po" />
                  Also reject the linked PO ({reviewDialog.rev.po_number || reviewDialog.rev.po_id.slice(-6)})
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" disabled={submitting} onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button
              className={`rounded-sm gap-1.5 ${reviewDialog?.action === "approve" ? "bg-[#137333] hover:bg-[#0E5C29] text-white" : "bg-[#C5221F] hover:bg-[#A80D0B] text-white"}`}
              disabled={submitting}
              onClick={submitReview}
              data-testid="revision-submit-btn"
            >
              {submitting ? "Saving…" : reviewDialog?.action === "approve" ? "Approve & bump BOQ" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Revision Justification modal (used by PO create) ---------------- */
export function BudgetRevisionRequestModal({ open, onOpenChange, check, category, projectId, onCreated }) {
  const [reason, setReason] = useState("price_hike");
  const [remark, setRemark] = useState("");
  const [requested, setRequested] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && check) {
      // Suggest the new_total_after as a reasonable default
      setRequested(String(check.new_total_after || ""));
      setRemark("");
      setReason("price_hike");
    }
  }, [open, check]);

  const submit = async () => {
    if (!remark.trim()) { toast.error("Detailed remark is required"); return; }
    if (!requested || Number(requested) <= Number(check.boq_limit)) {
      toast.error("Requested total must exceed the current BOQ limit");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/budget-revisions", {
        project_id: projectId,
        category,
        current_limit: check.boq_limit,
        requested_total: Number(requested),
        reason_category: reason,
        remark,
      });
      toast.success("Revision request created — PO will be routed to owner review");
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (!open || !check) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="revision-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2 text-[#C5221F]">
            <WarningCircle size={22} weight="fill" /> Over-Budget — Revision Required
          </DialogTitle>
          <DialogDescription>
            This PO would push <span className="font-medium text-neutral-900">{category}</span> past the BOQ limit for this project. Submit a revision justification to route it to the owner for review.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <MetricBox label="Current BOQ Limit" value={fmt(check.boq_limit)} />
            <div className="flex items-center justify-center"><ArrowRight size={20} className="text-neutral-400" /></div>
            <MetricBox label="New Requested Total" value={fmt(check.new_total_after)} accent="text-[#C5221F]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 mb-1 block">Reason Category *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="rounded-sm h-10" data-testid="revision-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REASON_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 mb-1 block">Requested New Limit *</label>
            <Input type="number" step="0.01" value={requested} onChange={(e) => setRequested(e.target.value)} className="rounded-sm tabular-nums" data-testid="revision-requested" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 mb-1 block">Detailed Remark *</label>
            <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={4} className="rounded-sm" placeholder="Why do we need to increase the budget? Vendor quotes, market changes, scope additions…" data-testid="revision-remark" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="rounded-sm bg-primary hover:bg-[#0039CC] gap-1.5" onClick={submit} disabled={submitting} data-testid="revision-request-submit">
            <TrendUp size={14} weight="bold" />
            {submitting ? "Submitting…" : "Submit for Owner Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricBox({ label, value, accent }) {
  return (
    <div className="border border-neutral-200 rounded-sm p-3 bg-neutral-50/50">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${accent || "text-neutral-900"}`}>{value}</div>
    </div>
  );
}
